// ReAct loading-step accordion / data-block rendering (extracted from ui.js, B4).
// scrollToBottom lives in ui.js and is called at runtime (safe ui.js<->ui-steps cycle).
import { scrollToBottom, hideLoading } from './ui.js';

function _buildStepRow(entry, hasTokens) {
    const row = document.createElement('div');
    const isPending = entry.pending === true;
    row.className = isPending
        ? 'step-row step-pending'
        : (entry.success ? 'step-row' : 'step-row step-failed');
    if (hasTokens) row.classList.add('has-tokens');
    if (entry.call_id) row.dataset.callId = entry.call_id;

    const stepTokens = (entry.prompt_tokens || 0) + (entry.completion_tokens || 0);
    const tokenStr = stepTokens >= 1000 ? (stepTokens / 1000).toFixed(1) + 'k' : stepTokens > 0 ? String(stepTokens) : '';

    // Pending rows show a spinner glyph in the timing slot and a hollow dot
    // for the success indicator. They get upgraded in place by updateLoadingStep
    // when tool_complete arrives.
    const timingHtml = isPending
        ? '<span class="step-timing step-timing-pending">…</span>'
        : `<span class="step-timing">${entry.duration_ms >= 1000 ? (entry.duration_ms / 1000).toFixed(1) + 's' : entry.duration_ms + 'ms'}</span>`;
    const statusHtml = isPending
        ? '<span class="step-pending-dot">○</span>'
        : `<span class="${entry.success ? 'step-ok' : 'step-fail'}">${entry.success ? '✓' : '✗'}</span>`;

    row.innerHTML = `
        <span class="step-num">${entry.step}</span>
        <span class="step-tool">${entry.tool_label || entry.tool}</span>
        <span class="step-args" title="${(entry.args_summary || '').replace(/"/g, '&quot;')}">${entry.args_summary || ''}</span>
        ${hasTokens ? `<span class="step-tokens">${tokenStr}</span>` : ''}
        ${timingHtml}
        ${statusHtml}
    `;
    return row;
}

/**
 * Build accordion header summary text from step count + failures
 */
function _buildAccordionSummary(toolCount, failureCount) {
    // A turn that answered straight from context ran no tools — say that
    // instead of the misleading "1 tool call".
    if (toolCount === 0) return 'Answered directly (no tools)';
    const callWord = toolCount === 1 ? 'tool call' : 'tool calls';
    return failureCount
        ? `${toolCount} ${callWord} (${failureCount} failed)`
        : `${toolCount} ${callWord}`;
}

/**
 * Add a step row to the live loading indicator accordion.
 * Shows the expand chevron on first step and updates the count badge.
 */
export function addLoadingStep(stepEntry) {
    const loader = document.getElementById('loading-indicator');
    if (!loader) return;

    const grid = loader.querySelector('.step-grid');
    if (!grid) return;

    // Check if any existing row has tokens to decide column layout
    const existingRows = grid.querySelectorAll('.step-row');
    const hasTokens = (stepEntry.prompt_tokens || 0) + (stepEntry.completion_tokens || 0) > 0 ||
        Array.from(existingRows).some(r => r.classList.contains('has-tokens'));

    const row = _buildStepRow(stepEntry, hasTokens);
    grid.appendChild(row);

    // Show expand button on first step
    const expandBtn = loader.querySelector('.loading-expand-btn');
    if (expandBtn) {
        expandBtn.style.display = 'inline-flex';
    }

    // Update count badge
    const countBadge = loader.querySelector('.loading-step-count');
    const totalSteps = grid.querySelectorAll('.step-row').length;
    if (countBadge) {
        countBadge.textContent = totalSteps;
    }

    // Auto-scroll step container if expanded
    const stepsContainer = loader.querySelector('.loading-steps');
    if (stepsContainer && loader.classList.contains('expanded')) {
        stepsContainer.scrollTop = stepsContainer.scrollHeight;
    }

    scrollToBottom();
}

/**
 * Update the args text of a pending placeholder row to show sub-step
 * progress (e.g. "Searching 2/5…", "Fetching 8 pages…"). Used by tools
 * that take a long time to run and want to surface what they're doing.
 *
 * Matches the row by call_id. No-op if the row no longer exists or is
 * no longer pending — late substep events arriving after tool_complete
 * are silently dropped.
 */
export function updateStepSubstep(callId, message) {
    if (!callId || !message) return;
    const loader = document.getElementById('loading-indicator');
    if (!loader) return;
    const grid = loader.querySelector('.step-grid');
    if (!grid) return;
    const row = grid.querySelector(`.step-row.step-pending[data-call-id="${CSS.escape(String(callId))}"]`);
    if (!row) return;
    const argsEl = row.querySelector('.step-args');
    if (!argsEl) return;
    // Cache the original args (the query) on first substep so we don't lose it
    if (argsEl.dataset.originalArgs === undefined) {
        argsEl.dataset.originalArgs = argsEl.textContent || '';
    }
    argsEl.textContent = message;
    argsEl.title = message;
    argsEl.classList.add('step-args-substep');
}

/**
 * Upgrade an existing pending step row in place when tool_complete arrives.
 * Matches by call_id (set on the row's data attribute when the placeholder
 * was added). Falls back to addLoadingStep if no matching row is found —
 * this handles older clients/replays that never received a found_tool event.
 */
export function updateLoadingStep(stepEntry) {
    const loader = document.getElementById('loading-indicator');
    if (!loader || !stepEntry) {
        if (stepEntry) addLoadingStep(stepEntry);
        return;
    }

    const callId = stepEntry.call_id;
    const grid = loader.querySelector('.step-grid');
    const existingRow = callId && grid
        ? grid.querySelector(`.step-row[data-call-id="${CSS.escape(String(callId))}"]`)
        : null;

    if (!existingRow) {
        // No placeholder — add it as a new row (legacy fallback)
        addLoadingStep(stepEntry);
        return;
    }

    // Replace the row in place, preserving its position so parallel calls
    // stay in dispatch order.
    const hasTokens = (stepEntry.prompt_tokens || 0) + (stepEntry.completion_tokens || 0) > 0
        || Array.from(grid.querySelectorAll('.step-row')).some(r => r.classList.contains('has-tokens'));
    const newRow = _buildStepRow(stepEntry, hasTokens);
    existingRow.replaceWith(newRow);

    // Auto-scroll step container if expanded
    const stepsContainer = loader.querySelector('.loading-steps');
    if (stepsContainer && loader.classList.contains('expanded')) {
        stepsContainer.scrollTop = stepsContainer.scrollHeight;
    }
}

/**
 * Solidify steps into a permanent accordion on the completed message.
 * Replaces hideLoading() + showTurnSummary() + showStepTimeline().
 * Uses the stepLog data array (not DOM) since the loading indicator may
 * already be removed by the time streaming completes.
 */
export function solidifyLoadingSteps(messageEl, stepLog) {
    // Always remove the loading indicator if still present
    hideLoading();

    // Delegate to showStepAccordion for the permanent display
    if (stepLog && stepLog.length > 0 && messageEl) {
        showStepAccordion(messageEl, stepLog);
    }
}

/**
 * Render verbatim data blocks from management tool raw output.
 * Inserted after step accordion, before message content.
 */
export function renderDataBlocks(messageEl, dataBlocks) {
    if (!dataBlocks || dataBlocks.length === 0 || !messageEl) return;

    // Remove existing container (idempotent for restore)
    const existing = messageEl.querySelector('.data-blocks-container');
    if (existing) existing.remove();

    const container = document.createElement('div');
    container.className = 'data-blocks-container';

    for (const block of dataBlocks) {
        const blockEl = document.createElement('div');
        blockEl.className = 'data-block';

        const header = document.createElement('div');
        header.className = 'data-block-header';

        const label = document.createElement('span');
        label.className = 'data-block-label';
        label.textContent = block.tool_label;

        const args = document.createElement('span');
        args.className = 'data-block-args';
        args.textContent = block.args_summary;

        const copyBtn = document.createElement('button');
        copyBtn.className = 'data-block-copy';
        copyBtn.title = 'Copy to clipboard';
        copyBtn.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
        </svg>`;
        copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(block.raw_output).then(() => {
                copyBtn.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>`;
                setTimeout(() => {
                    copyBtn.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
                }, 1500);
            });
        });

        header.appendChild(label);
        header.appendChild(args);
        header.appendChild(copyBtn);
        blockEl.appendChild(header);

        const content = document.createElement('pre');
        content.className = 'data-block-content';
        content.textContent = block.raw_output;
        blockEl.appendChild(content);

        container.appendChild(blockEl);
    }

    // Insert after step accordion if present, else after header, else before content
    const accordion = messageEl.querySelector('.step-accordion');
    const messageContent = messageEl.querySelector('.message-content');
    if (accordion) {
        accordion.after(container);
    } else if (messageContent) {
        messageEl.insertBefore(container, messageContent);
    } else {
        messageEl.appendChild(container);
    }
}

/**
 * Show step accordion on a restored message (from saved step_log data)
 */
export function showStepAccordion(messageEl, stepLog) {
    if (!stepLog || stepLog.length === 0 || !messageEl) return;

    // Remove any existing accordion
    const existing = messageEl.querySelector('.step-accordion');
    if (existing) existing.remove();

    const hasTokens = stepLog.some(e => (e.prompt_tokens || 0) + (e.completion_tokens || 0) > 0);
    const failureCount = stepLog.filter(e => !e.success).length;
    // Count real tool calls only — phase rows ("Deciding tool", "Answered
    // from context", planning) are not tool invocations and shouldn't be
    // tallied as such in the header.
    const toolCount = stepLog.filter(e => !e.phase).length;

    const accordion = document.createElement('div');
    accordion.className = 'step-accordion';

    const header = document.createElement('div');
    header.className = 'step-accordion-header';
    header.innerHTML = `
        <svg class="step-accordion-chevron" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
        </svg>
        <span>${_buildAccordionSummary(toolCount, failureCount)}</span>
    `;
    accordion.appendChild(header);

    const grid = document.createElement('div');
    grid.className = 'step-grid step-accordion-grid';
    grid.style.display = 'none';
    for (const entry of stepLog) {
        grid.appendChild(_buildStepRow(entry, hasTokens));
    }
    accordion.appendChild(grid);

    // Wire toggle
    header.addEventListener('click', () => {
        const isExpanded = accordion.classList.toggle('expanded');
        grid.style.display = isExpanded ? 'block' : 'none';
    });

    // Insert after thinking section (if present), else after header, else before content
    const thinkingSection = messageEl.querySelector('.thinking-section');
    const messageHeader = messageEl.querySelector('.message-header');
    const messageContent = messageEl.querySelector('.message-content');
    if (thinkingSection) {
        thinkingSection.after(accordion);
    } else if (messageHeader) {
        messageHeader.after(accordion);
    } else if (messageContent) {
        messageEl.insertBefore(accordion, messageContent);
    } else {
        messageEl.prepend(accordion);
    }

}

/**
 * Update extraction status on a message's action bar.
 * Called when extraction completes to enable the delete button.
 */
